import React, { useMemo, useRef, useState } from 'react';
import './StoreForm.css';
import { useToast } from '@/hooks/use-toast';
import { Store, Clock, DollarSign, Phone, Image, FileText, X, MapPin } from 'lucide-react';
import { STORE_CATEGORIES, DEFAULT_OPENING_HOURS, DAYS_OF_WEEK } from '@/services/stores';
import { DEFAULT_LOGO, DEFAULT_BANNER } from '@/lib/storeImages';
import { reverseGeocode } from '@/services/routes';

const GEOCODE_REFRESH_MS = Number(process.env.REACT_APP_GEOCODE_REFRESH_MS) || 15000;

function parseAddress(rawAddress) {
  if (!rawAddress || typeof rawAddress !== 'string') {
    return {
      street: '',
      neighborhood: '',
      number: '',
      zip_code: '',
      city: '',
      reference: '',
    };
  }

  const value = rawAddress.trim();
  const directParts = value.split('|').map((item) => item.trim());
  if (directParts.length === 6) {
    return {
      street: directParts[0] || '',
      neighborhood: directParts[1] || '',
      number: directParts[2] || '',
      zip_code: directParts[3] || '',
      city: directParts[4] || '',
      reference: directParts[5] || '',
    };
  }

  return {
    street: value,
    neighborhood: '',
    number: '',
    zip_code: '',
    city: '',
    reference: '',
  };
}

function buildAddressString(addressData) {
  const parts = [
    addressData.street,
    addressData.neighborhood,
    addressData.number,
    addressData.zip_code,
    addressData.city,
    addressData.reference,
  ].map((item) => String(item || '').trim());

  if (parts.every((item) => !item)) {
    return '';
  }

  return parts.join(' | ');
}

function formatAddressPreview(addressData) {
  const street = String(addressData.street || '').trim();
  const number = String(addressData.number || '').trim();
  const neighborhood = String(addressData.neighborhood || '').trim();
  const city = String(addressData.city || '').trim();
  const zipCode = String(addressData.zip_code || '').trim();
  const reference = String(addressData.reference || '').trim();

  const line1 = [street, number].filter(Boolean).join(', ');
  const line2 = [neighborhood, city].filter(Boolean).join(' - ');
  const line3 = [zipCode ? `CEP ${zipCode}` : '', reference ? `Ref: ${reference}` : '']
    .filter(Boolean)
    .join(' | ');

  const preview = [line1, line2, line3].filter(Boolean).join(' | ');
  return preview || 'Endereco nao informado';
}

function splitCategoryValues(value) {
  return String(value || '')
    .split(/[|,;/]+/g)
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseInitialCategories(initialData) {
  const fromArray = Array.isArray(initialData?.categories)
    ? initialData.categories
    : splitCategoryValues(initialData?.categories);
  const fromSingle = splitCategoryValues(initialData?.category);
  const combined = [...fromArray, ...fromSingle];

  const deduped = [];
  const seen = new Set();
  combined.forEach((item) => {
    const key = String(item || '').trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    deduped.push(item.trim());
  });

  return deduped;
}

export default function StoreForm({ initialData, onSubmit, onCancel, isEditing = false, userEmail }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const [logoFileData, setLogoFileData] = useState('');
  const [bannerFileData, setBannerFileData] = useState('');
  const [locatingStore, setLocatingStore] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);
  const [reverseError, setReverseError] = useState('');
  const reverseRequestRef = useRef({ key: '', ts: 0 });



  const initialAddress = useMemo(() => parseAddress(initialData?.address || initialData?.endereco), [initialData?.address, initialData?.endereco]);
  const initialCategories = useMemo(
    () => parseInitialCategories(initialData),
    [initialData]
  );
  const initialCourierMode = useMemo(() => {
    const fromData = initialData?.courier_mode;
    const fromOpeningHours = initialData?.opening_hours?.delivery_settings?.courier_mode;
    const normalized = String(fromData || fromOpeningHours || 'own').trim().toLowerCase();
    return normalized === 'own' ? 'own' : 'platform';
  }, [initialData]);
  const initialAllowPlatformOnDemand = useMemo(() => {
    if (typeof initialData?.allow_platform_on_demand === 'boolean') {
      return initialData.allow_platform_on_demand;
    }
    return Boolean(initialData?.opening_hours?.delivery_settings?.allow_platform_on_demand);
  }, [initialData]);

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    category: initialCategories[0] || initialData?.category || '',
    categories: initialCategories,
    email: initialData?.email || userEmail || '',
    description: initialData?.description || '',
    logo_url: initialData?.logo_url || '',
    banner_url: initialData?.banner_url || '',
    phone: initialData?.phone || '',
    lat: (initialData?.lat ?? ''),
    lng: (initialData?.lng ?? ''),
    address_street: initialAddress.street,
    address_neighborhood: initialAddress.neighborhood,
    address_number: initialAddress.number,
    address_zip_code: initialAddress.zip_code,
    address_city: initialAddress.city,
    address_reference: initialAddress.reference,
    delivery_fee: Number(initialData?.delivery_fee || 0),
    delivery_time: initialData?.delivery_time || initialData?.opening_hours?.delivery_time || '30-40 min',
    min_order_value: Number(initialData?.min_order_value || 0),
    opening_hours: initialData?.opening_hours || DEFAULT_OPENING_HOURS,
    courier_mode: initialCourierMode,
    allow_platform_on_demand: initialAllowPlatformOnDemand,
  });

  const [errors, setErrors] = useState({});
  const addressPreview = formatAddressPreview({
    street: formData.address_street,
    neighborhood: formData.address_neighborhood,
    number: formData.address_number,
    zip_code: formData.address_zip_code,
    city: formData.address_city,
    reference: formData.address_reference,
  });

  const logoPreview = logoFileData || formData.logo_url || DEFAULT_LOGO;
  const bannerPreview = bannerFileData || formData.banner_url || DEFAULT_BANNER;

  function handleChange(field, value) {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (field === 'logo_url') {
      setLogoFileData('');
    }

    if (field === 'banner_url') {
      setBannerFileData('');
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  }

  const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

  function handleCategoryToggle(category, checked) {
    setFormData((prev) => {
      const current = Array.isArray(prev.categories) ? prev.categories : [];
      const hasCategory = current.includes(category);
      let nextCategories = current;

      if (checked && !hasCategory) {
        nextCategories = [...current, category];
      } else if (!checked && hasCategory) {
        nextCategories = current.filter((item) => item !== category);
      }

      return {
        ...prev,
        categories: nextCategories,
        category: nextCategories[0] || '',
      };
    });

    if (errors.category) {
      setErrors((prev) => ({ ...prev, category: null }));
    }
  }

  function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }


  function parseCoordinate(value) {
    const raw = String(value ?? '').trim().replace(',', '.');
    if (!raw) return null;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
  }

  async function reverseGeocodeAddress(lat, lng) {
    const roundedLat = Number(lat.toFixed(5));
    const roundedLng = Number(lng.toFixed(5));
    const key = `${roundedLat},${roundedLng}`;
    const now = Date.now();
    if (reverseRequestRef.current.key === key && (now - reverseRequestRef.current.ts) < GEOCODE_REFRESH_MS) {
      return;
    }

    
    reverseRequestRef.current = { key, ts: now };
    setReverseLoading(true);
    setReverseError('');

    try {
      const data = await reverseGeocode({ lat: roundedLat, lng: roundedLng });
      if (!data) return;

      setFormData((prev) => ({
        ...prev,
        lat: roundedLat.toFixed(6),
        lng: roundedLng.toFixed(6),
        address_street: data.street || prev.address_street,
        address_number: data.number || prev.address_number,
        address_neighborhood: data.neighborhood || prev.address_neighborhood,
        address_city: data.city || prev.address_city,
        address_zip_code: data.zipcode || prev.address_zip_code,
        address_reference: data.label || prev.address_reference,
      }));
    } catch (err) {
      setReverseError(err?.message || 'Nao foi possivel preencher o endereco automaticamente.');
    } finally {
      setReverseLoading(false);
    }
  }

  function handleUseCurrentLocation() {
    if (!navigator.geolocation) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Geolocalizacao nao suportada neste dispositivo.',
      });
      return;
    }

    setLocatingStore(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setFormData((prev) => ({
          ...prev,
          lat: lat.toFixed(6),
          lng: lng.toFixed(6),
        }));
        reverseGeocodeAddress(lat, lng);
        setLocatingStore(false);
      },
      () => {
        setLocatingStore(false);
        toast({
          variant: 'destructive',
          title: 'Erro',
          description: 'Nao foi possivel obter sua localizacao.',
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }

  async function handleImageFileChange(kind, event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({
        variant: 'destructive',
        title: 'Arquivo invalido',
        description: 'Selecione uma imagem valida.',
      });
      event.target.value = '';
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      toast({
        variant: 'destructive',
        title: 'Imagem muito grande',
        description: 'Use uma imagem de ate 2MB.',
      });
      event.target.value = '';
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(file);
      if (kind === 'logo') {
        setLogoFileData(String(dataUrl));
        setFormData((prev) => ({ ...prev, logo_url: '' }));
      } else {
        setBannerFileData(String(dataUrl));
        setFormData((prev) => ({ ...prev, banner_url: '' }));
      }
    } catch (_) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Nao foi possivel ler a imagem.',
      });
    }
  }

  function clearImage(kind) {
    if (kind === 'logo') {
      setLogoFileData('');
    } else {
      setBannerFileData('');
    }
  }

  function handleOpeningHoursChange(day, field, value) {
    setFormData((prev) => ({
      ...prev,
      opening_hours: {
        ...prev.opening_hours,
        [day]: {
          ...prev.opening_hours[day],
          [field]: value,
        },
      },
    }));
  }

  function validateForm() {
    const nextErrors = {};

    if (!formData.name.trim()) {
      nextErrors.name = 'Nome da loja e obrigatorio';
    }

    if (!Array.isArray(formData.categories) || formData.categories.length === 0) {
      nextErrors.category = 'Categoria e obrigatoria';
    }

    if (!formData.phone.trim()) {
      nextErrors.phone = 'Telefone e obrigatorio';
    }

    if (Number(formData.delivery_fee) < 0) {
      nextErrors.delivery_fee = 'Taxa de entrega nao pode ser negativa';
    }

    if (Number(formData.min_order_value) < 0) {
      nextErrors.min_order_value = 'Valor minimo nao pode ser negativo';
    }

    const latValue = parseCoordinate(formData.lat);
    const lngValue = parseCoordinate(formData.lng);

    if (latValue === null || latValue < -90 || latValue > 90) {
      nextErrors.lat = 'Latitude invalida';
    }

    if (lngValue === null || lngValue < -180 || lngValue > 180) {
      nextErrors.lng = 'Longitude invalida';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!validateForm()) {
      toast({
        variant: 'destructive',
        title: 'Erro de validacao',
        description: 'Por favor, corrija os erros no formulario.',
      });
      return;
    }

    const normalizedCategories = (Array.isArray(formData.categories) ? formData.categories : [])
      .map((item) => String(item || '').trim())
      .filter(Boolean);
    const primaryCategory = normalizedCategories[0] || String(formData.category || '').trim();

    const payload = {
      name: formData.name,
      category: primaryCategory,
      categories: normalizedCategories,
      email: formData.email,
      description: formData.description,
      logo_url: logoFileData || formData.logo_url,
      banner_url: bannerFileData || formData.banner_url,
      phone: formData.phone,
      address: buildAddressString({
        street: formData.address_street,
        neighborhood: formData.address_neighborhood,
        number: formData.address_number,
        zip_code: formData.address_zip_code,
        city: formData.address_city,
        reference: formData.address_reference,
      }),
      address_street: formData.address_street,
      address_neighborhood: formData.address_neighborhood,
      address_number: formData.address_number,
      address_zip_code: formData.address_zip_code,
      address_city: formData.address_city,
      address_reference: formData.address_reference,
      lat: parseCoordinate(formData.lat),
      lng: parseCoordinate(formData.lng),
      delivery_fee: Number(formData.delivery_fee || 0),
      delivery_time: formData.delivery_time,
      min_order_value: Number(formData.min_order_value || 0),
      opening_hours: formData.opening_hours,
      courier_mode: formData.courier_mode,
      allow_platform_on_demand: Boolean(formData.allow_platform_on_demand),
    };

    setLoading(true);
    try {
      await onSubmit(payload);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.message || 'Nao foi possivel salvar a loja.',
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="store-form">
      <div className="form-header">
        <h2>{isEditing ? 'Editar Loja' : 'Cadastrar Loja'}</h2>
        {onCancel && (
          <button type="button" className="btn-close-form" onClick={onCancel}>
            <X size={24} />
          </button>
        )}
      </div>

      <div className="form-sections">
        <section className="form-section">
          <h3>
            <Store size={20} />
            Informacoes Basicas
          </h3>

          <div className="form-group">
            <label>Nome da Loja *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Ex: Pizzaria do Joao"
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label>Categorias * (selecione uma ou mais)</label>
            <div className={`category-picker ${errors.category ? 'error' : ''}`}>
              {STORE_CATEGORIES.map((cat) => {
                const checked = Array.isArray(formData.categories) && formData.categories.includes(cat);
                return (
                  <label key={cat} className={`category-option ${checked ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => handleCategoryToggle(cat, e.target.checked)}
                    />
                    <span>{cat}</span>
                  </label>
                );
              })}
            </div>
            {Array.isArray(formData.categories) && formData.categories.length > 0 && (
              <p className="category-selected-text">
                Selecionadas: {formData.categories.join(', ')}
              </p>
            )}
            {errors.category && <span className="error-message">{errors.category}</span>}
          </div>

          <div className="form-group">
            <label>Entrega por</label>
            <div className="delivery-mode-picker">
              <label className={`delivery-mode-option ${formData.courier_mode === 'platform' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="courier_mode"
                  value="platform"
                  checked={formData.courier_mode === 'platform'}
                  onChange={() =>
                    setFormData((prev) => ({
                      ...prev,
                      courier_mode: 'platform',
                      allow_platform_on_demand: false,
                    }))
                  }
                />
                <span>Motoboys do Correfood</span>
              </label>
              <label className={`delivery-mode-option ${formData.courier_mode === 'own' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="courier_mode"
                  value="own"
                  checked={formData.courier_mode === 'own'}
                  onChange={() =>
                    setFormData((prev) => ({
                      ...prev,
                      courier_mode: 'own',
                    }))
                  }
                />
                <span>Motoboys proprios da loja</span>
              </label>
            </div>

            <label className={`delivery-fallback-option ${formData.courier_mode !== 'own' ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                checked={Boolean(formData.allow_platform_on_demand)}
                disabled={formData.courier_mode !== 'own'}
                onChange={(e) => handleChange('allow_platform_on_demand', e.target.checked)}
              />
              <span>Permitir chamar motoboy do Correfood quando faltar motoboy proprio</span>
            </label>
          </div>

          <div className="form-group">
            <label>E-mail da Loja</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="contato@minhaloja.com"
            />
            <p className="text-xs text-gray-500 mt-1">Se estiver vazio, usaremos o e-mail da sua conta.</p>
          </div>

          <div className="form-group">
            <label>
              <Phone size={16} />
              Telefone *
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="(00) 00000-0000"
              className={errors.phone ? 'error' : ''}
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label>
              <MapPin size={16} />
              Endereco da loja
            </label>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Rua</label>
              <input type="text" value={formData.address_street} onChange={(e) => handleChange('address_street', e.target.value)} placeholder="Rua" />
            </div>
            <div className="form-group">
              <label>Numero</label>
              <input type="text" value={formData.address_number} onChange={(e) => handleChange('address_number', e.target.value)} placeholder="Numero" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Bairro</label>
              <input type="text" value={formData.address_neighborhood} onChange={(e) => handleChange('address_neighborhood', e.target.value)} placeholder="Bairro" />
            </div>
            <div className="form-group">
              <label>CEP</label>
              <input type="text" value={formData.address_zip_code} onChange={(e) => handleChange('address_zip_code', e.target.value)} placeholder="00000-000" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Cidade</label>
              <input type="text" value={formData.address_city} onChange={(e) => handleChange('address_city', e.target.value)} placeholder="Cidade" />
            </div>
            <div className="form-group">
              <label>Referencia</label>
              <input type="text" value={formData.address_reference} onChange={(e) => handleChange('address_reference', e.target.value)} placeholder="Ponto de referencia" />
            </div>
          </div>

          <div className="form-group">
            <label>Previa do endereco</label>
            <div className="address-preview-box">{addressPreview}</div>
          </div>

          <div className="form-group">
            <label>Localizacao da loja *</label>
            <div className="form-row">
              <div className="form-group">
                <label>Latitude</label>
                <input
                  type="text"
                  value={formData.lat}
                  onChange={(e) => handleChange('lat', e.target.value)}
                  placeholder="-22.123456"
                  className={errors.lat ? 'error' : ''}
                />
                {errors.lat && <span className="error-message">{errors.lat}</span>}
              </div>
              <div className="form-group">
                <label>Longitude</label>
                <input
                  type="text"
                  value={formData.lng}
                  onChange={(e) => handleChange('lng', e.target.value)}
                  placeholder="-52.123456"
                  className={errors.lng ? 'error' : ''}
                />
                {errors.lng && <span className="error-message">{errors.lng}</span>}
              </div>
            </div>
            <button
              type="button"
              className="btn-cancel"
              style={{ width: 'fit-content', marginTop: '8px' }}
              onClick={handleUseCurrentLocation}
              disabled={locatingStore}
            >
              {locatingStore ? 'Obtendo localizacao...' : 'Usar minha localizacao'}
            </button>
            {reverseLoading && <p className="text-xs text-gray-500 mt-1">Preenchendo endereco automaticamente...</p>}
            {reverseError && <p className="text-xs text-red-600 mt-1">{reverseError}</p>}
            <p className="text-xs text-gray-500 mt-1">Necessario para liberar pedidos no raio do motoboy.</p>
          </div>

          <div className="form-group">
            <label>
              <FileText size={16} />
              Descricao
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Descreva sua loja..."
              rows="4"
            />
          </div>
        </section>

        <section className="form-section">
          <h3>
            <Image size={20} />
            Imagens
          </h3>

          <div className="form-group">
            <label>Logo (URL)</label>
            <input
              type="url"
              value={formData.logo_url}
              onChange={(e) => handleChange('logo_url', e.target.value)}
              placeholder="https://exemplo.com/logo.png"
            />
            <label style={{ marginTop: '8px' }}>Ou anexe o logotipo</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageFileChange('logo', e)}
            />
            <div className="image-preview">
              <img src={logoPreview} alt="Logo preview" />
            </div>
            {logoFileData && (
              <button
                type="button"
                className="btn-cancel"
                style={{ width: 'fit-content', marginTop: '8px' }}
                onClick={() => clearImage('logo')}
              >
                Remover anexo
              </button>
            )}
          </div>

          <div className="form-group">
            <label>Banner (URL)</label>
            <input
              type="url"
              value={formData.banner_url}
              onChange={(e) => handleChange('banner_url', e.target.value)}
              placeholder="https://exemplo.com/banner.png"
            />
            <label style={{ marginTop: '8px' }}>Ou anexe o banner</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageFileChange('banner', e)}
            />
            <div className="image-preview banner">
              <img src={bannerPreview} alt="Banner preview" />
            </div>
            {bannerFileData && (
              <button
                type="button"
                className="btn-cancel"
                style={{ width: 'fit-content', marginTop: '8px' }}
                onClick={() => clearImage('banner')}
              >
                Remover anexo
              </button>
            )}
          </div>
        </section>

        <section className="form-section">
          <h3>
            <DollarSign size={20} />
            Valores
          </h3>

          <div className="form-row">
            <div className="form-group">
              <label>Taxa de Entrega (R$)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.delivery_fee}
                onChange={(e) => handleChange('delivery_fee', e.target.value)}
                className={errors.delivery_fee ? 'error' : ''}
              />
              {errors.delivery_fee && <span className="error-message">{errors.delivery_fee}</span>}
            </div>

            <div className="form-group">
              <label>Pedido Minimo (R$)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.min_order_value}
                onChange={(e) => handleChange('min_order_value', e.target.value)}
                className={errors.min_order_value ? 'error' : ''}
              />
              {errors.min_order_value && <span className="error-message">{errors.min_order_value}</span>}
            </div>
          </div>

          <div className="form-group">
            <label>Tempo de Entrega</label>
            <input
              type="text"
              value={formData.delivery_time || ''}
              onChange={(e) => handleChange('delivery_time', e.target.value)}
              placeholder="Ex: 30-40 min"
            />
          </div>
        </section>

        <section className="form-section">
          <h3>
            <Clock size={20} />
            Horario de Funcionamento
          </h3>

          <div className="opening-hours">
            {Object.entries(DAYS_OF_WEEK).map(([key, label]) => (
              <div key={key} className="day-schedule">
                <div className="day-header">
                  <label className="day-label">
                    <input
                      type="checkbox"
                      checked={!formData.opening_hours[key]?.closed}
                      onChange={(e) => handleOpeningHoursChange(key, 'closed', !e.target.checked)}
                    />
                    <span>{label}</span>
                  </label>
                </div>

                {!formData.opening_hours[key]?.closed && (
                  <div className="time-inputs">
                    <input
                      type="time"
                      value={formData.opening_hours[key]?.open || '09:00'}
                      onChange={(e) => handleOpeningHoursChange(key, 'open', e.target.value)}
                    />
                    <span>ate</span>
                    <input
                      type="time"
                      value={formData.opening_hours[key]?.close || '22:00'}
                      onChange={(e) => handleOpeningHoursChange(key, 'close', e.target.value)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="form-actions">
        {onCancel && (
          <button type="button" className="btn-cancel" onClick={onCancel}>
            Cancelar
          </button>
        )}
        <button type="submit" className="btn-submit" disabled={loading}>
          {loading ? 'Salvando...' : (isEditing ? 'Salvar Alteracoes' : 'Cadastrar Loja')}
        </button>
      </div>
    </form>
  );
}



